import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import Logo from '@/components/Logo';
import { Loader2 } from 'lucide-react';

export default function SignupPage() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: params.get('name') || '',
    email: params.get('email') || '',
    password: '',
    role: params.get('role') === 'commercial' ? 'commercial' : 'customer',
    phone: '',
    company: '',
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await signup(form);
      toast.success(`Welcome to Royal Palm, ${u.name.split(' ')[0]}!`);
      nav(u.role === 'commercial' ? '/commercial-portal' : '/portal');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Signup failed');
    } finally { setLoading(false); }
  };

  return (
    <Layout hideFooter>
      <section className="max-w-md mx-auto px-4 sm:px-6 py-12">
        <div className="flex flex-col items-center gap-3 mb-6">
          <img src="/logo.png" alt="Royal Palm Services" className="h-24 w-auto object-contain" />
          <div className="font-headline text-2xl text-[#17352D]">Create account</div>
        </div>
        <form onSubmit={submit} className="rounded-2xl border border-[#E6E9EE] bg-white p-6 space-y-3" data-testid="signup-form">
          <div className="flex gap-2">
            {['customer', 'commercial'].map(r => (
              <button key={r} type="button" onClick={() => setForm({ ...form, role: r })}
                data-testid={`signup-role-${r}`}
                className={`flex-1 rounded-xl border px-3 py-2 text-sm capitalize ${form.role === r ? 'border-[#17352D] bg-[#F2F7F5] text-[#17352D] font-semibold' : 'border-[#E6E9EE] text-slate-600'}`}>{r === 'commercial' ? 'Business / Commercial' : 'Homeowner'}</button>
            ))}
          </div>
          <div><Label htmlFor="sn">Full name</Label><Input id="sn" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="mt-1 h-11 rounded-xl" data-testid="signup-name-input" /></div>
          {form.role === 'commercial' && (
            <div><Label htmlFor="sc">Company</Label><Input id="sc" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} className="mt-1 h-11 rounded-xl" data-testid="signup-company-input" /></div>
          )}
          <div><Label htmlFor="se">Email</Label><Input id="se" type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="mt-1 h-11 rounded-xl" data-testid="signup-email-input" /></div>
          <div><Label htmlFor="sp">Phone</Label><Input id="sp" type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="mt-1 h-11 rounded-xl" data-testid="signup-phone-input" /></div>
          <div><Label htmlFor="spw">Password</Label><Input id="spw" type="password" required minLength={6} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="mt-1 h-11 rounded-xl" data-testid="signup-password-input" /></div>
          <Button type="submit" disabled={loading} className="w-full rounded-xl bg-[#17352D] hover:bg-[#1E4338] text-white h-12" data-testid="signup-submit-button">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create account'}</Button>
          <p className="text-sm text-slate-600 text-center">Already a customer? <Link to="/login" className="text-[#17352D] font-semibold underline">Sign in</Link></p>
        </form>
      </section>
    </Layout>
  );
}
